'use client'

import { Fragment, type ReactNode } from 'react'
import Container from '@/components/structure/Container'
import AlternativeHero from '@/components/content/alternatives/alternative/hero/AlternativeHero'
import DealsTrending from '@/components/content/deals/root/DealsTrending'
import { isGoldOrPlatinumPartner } from '@/components/content/app/helpers'
import TabToggle from '@/components/navigation/Toggles'
import Tree from '@/components/content/sidebar/Tree'
import AlternativeSidebar from './AlternativeSidebar'
import Timestamp from '@/components/ui/Timestamp'
import Authors from '@/components/content/common/Authors'
import AppRanking, { AppRankingTitle } from '@/components/content/app/AppRanking'
import RichText from '@/components/content/common/RichText'
import Meta from '@/components/structure/Meta'
import ExploreButton from '@/components/content/common/ExploreButton'
import Videos from '@/components/content/videos/Videos'
import AppIcon from '@/components/content/app/AppIcon'
import Link from '@/components/ui/Link'
import Section from '@/components/content/common/Section'
import RelatedComparisons from '@/components/content/common/RelatedComparisons'
import PreferredSource from '@/components/ui/PreferredSource'
import { PageContextProvider } from '@/components/content/common/PageContext'
import { useDealsTab } from '@/components/content/common/Tabs'
import ComparisonThumbnail from '@/components/content/comparisons/root/ComparisonThumbnail'
import { FeaturedTagType } from '@/collections/FeaturedTag/constants'
import { Icon } from '@/icons/Icon'
import { usePageContext } from '@/components/content/common/PageContext'
import { generateMenu } from '@/helpers/menus'
import { orderAppsByRecommendation } from '@/components/content/best/helpers'
import { extractBlogSlugUrlFromFaq } from '@/lib/formatting'
import { getBrandColors, hasRichTextContent, isPopulatedObject, toSlug } from '@/lib/utils'
import { checkAppBranding } from '@/lib/formatting'
import { generateStructure, renderContent } from '@/lib/content'
import type { SectionItem, Block } from '@/types/app'
import type { BylineAuthor } from '@/lib/fetch-authors'
import type {
  App,
  Alternative,
  Category,
  AppCategoryRecommendation,
  Comparison,
  Video,
  VideoAppsApp,
  Review,
  Faq,
} from '@/payload-types'

function demoteHeadings(blocks: Block[]): Block[] {
  return blocks.map((block) => {
    if ('tag' in block && typeof block.tag === 'string' && /^h[1-5]$/.test(block.tag)) {
      const level = parseInt(block.tag.charAt(1), 10)
      return { ...block, tag: `h${Math.min(level + 1, 6)}` }
    }
    return block
  })
}

const DEMOTED_HEADING_STYLES =
  '[&_h3]:text-18 [&_h3]:font-bold md:[&_h3]:text-20 [&_h4]:text-18 [&_h4]:font-bold'

type AlternativeContainerProps = {
  app: App
  primaryCategory: Category
  appRecs: AppCategoryRecommendation[]
  videoEntries: { video: Video; videoApps: VideoAppsApp[] }[]
  faqs: Faq[]
  comparisons: Comparison[]
  modifiedTimestamp: string
  authors?: BylineAuthor[]
}

export default function AlternativeContainer(props: AlternativeContainerProps) {
  return (
    <PageContextProvider initialTab="Alternatives">
      <AlternativeContainerContent {...props} />
    </PageContextProvider>
  )
}

function AlternativeContainerContent({
  app,
  primaryCategory,
  appRecs,
  videoEntries,
  faqs,
  comparisons: serverComparisons,
  modifiedTimestamp,
  authors,
}: AlternativeContainerProps) {
  const videos = videoEntries.map((e) => e.video)
  const videoAppsMap = Object.fromEntries(videoEntries.map((e) => [e.video.id, e.videoApps]))
  const { pageTab, setPageTab, setPlayVideo } = usePageContext()
  const alternativeApps = appRecs.map((arc) => arc.app).filter((app) => isPopulatedObject<App>(app))

  const joinedApps = [...alternativeApps, app]

  const menu = generateMenu('app', {
    app: joinedApps,
    resetTab: () => setPageTab('Alternatives'),
    setPlayVideo: videos.length > 0 ? setPlayVideo : undefined,
  })
  const { orderedAppRecs, orderedApps, notableCategoryApps } = orderAppsByRecommendation(appRecs)
  const { items: deals, totalCount: totalDeals } = useDealsTab(joinedApps)

  // primaryCategory is populated without its appRecommendations join, so AppButtons
  // can't tell whether the category has a premium partner and always falls back to
  // categoryHasPremiumPartner = false — promoting bronze affiliates outbound ("Try")
  // instead of "Read review". Feed the already-fetched category app recommendations
  // (plus the subject app) in so the partnership-tier check matches Best/Compare.
  const categoryWithRecommendations: Category = {
    ...primaryCategory,
    appRecommendations: {
      ...primaryCategory.appRecommendations,
      docs: [...appRecs, { app } as AppCategoryRecommendation],
    },
  }

  const comparisonsSection: SectionItem[] = serverComparisons.map((comparison) => ({
    title: comparison.name ?? 'Untitled',
    content: (
      <div className="flex flex-col gap-4">
        <RichText content={(comparison.tldr?.root?.children as Block[]) ?? []} />
        <ComparisonThumbnail comparison={comparison} type={FeaturedTagType.ThumbnailPrimary} />
      </div>
    ),
  }))
  const totalComparisons = serverComparisons.length

  const [alternative] = (app.alternatives?.docs as Alternative[]) ?? []
  const publishedTimestamp = alternative?.createdAt
  const alternativeDescription = alternative?.description
  const finalVerdict = alternative?.verdict

  // Main Summary
  const showNotableMentions = primaryCategory && notableCategoryApps.length > 0

  const mainSummarySection: SectionItem[] = [
    {
      title: 'Summary',
      content: (
        <>
          {alternativeDescription && (
            <RichText content={alternativeDescription?.root?.children as Block[]} />
          )}

          <div className="flex w-full flex-col gap-8">
            <AppRanking
              apps={orderedAppRecs}
              otherApps={[]}
              category={categoryWithRecommendations}
              compareWithApp={app}
            />

            {showNotableMentions && (
              <AppRanking
                apps={notableCategoryApps}
                category={categoryWithRecommendations}
                compareWithApp={app}
                title={<AppRankingTitle title="Notable Mentions" />}
                rank="blank"
              />
            )}
          </div>
        </>
      ),
    },
  ]

  // Videos
  const videoSection: SectionItem[] =
    videos.length > 0
      ? [
          {
            title: videos.length === 1 ? 'Video' : 'Videos',
            content: <Videos videos={videos} videoAppsMap={videoAppsMap} />,
          },
        ]
      : []

  // Alternatives Summary
  const alternativesWithSummary: SectionItem[] = orderedApps
    .map((alternativeApp) => {
      const [review] =
        (alternativeApp.review?.docs ?? []).filter((doc): doc is Review =>
          isPopulatedObject<Review>(doc),
        ) ?? []

      if (!review?.alternativeSummary) {
        return null
      }

      const { backgroundColor } = getBrandColors(alternativeApp)
      const { appIcon, appGlyph } = checkAppBranding(alternativeApp)

      const summaryBlocks = review?.alternativeSummary?.root?.children as Block[] | undefined
      const { roots, preamble: preambleBlocks } = summaryBlocks?.length
        ? generateStructure({ content: summaryBlocks })
        : { roots: [], preamble: [] as Block[] }

      let alternativeSummary: SectionItem[] = []
      let altPreamble: ReactNode | null = null

      if (roots.length > 0) {
        const rendered = renderContent({ roots, preamble: preambleBlocks, rootTag: 3 })
        alternativeSummary = rendered.sections
        altPreamble = rendered.preamble
      } else if (summaryBlocks?.length) {
        alternativeSummary = [{ content: <RichText content={summaryBlocks} /> }] as SectionItem[]
      }

      return {
        app: alternativeApp,
        title: `${alternativeApp.name}`,
        slug: alternativeApp.slug,
        icon: (
          <Link href={`/apps/${alternativeApp.slug}`}>
            <AppIcon
              icon={appIcon}
              glyph={appGlyph}
              name={alternativeApp.name}
              background={backgroundColor}
              size={20}
            />
          </Link>
        ),
        content:
          alternativeSummary.length > 0 || altPreamble ? (
            <>
              {altPreamble}
              {alternativeSummary.map((section, i) => (
                <Fragment key={i}>{section.content as ReactNode}</Fragment>
              ))}
            </>
          ) : (
            <p>No summary available</p>
          ),
      }
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)

  // FAQs
  const faqSection: SectionItem[] =
    faqs && faqs.length > 0
      ? [
          {
            title: `${app.name} FAQs`,
            content: faqs?.map((faq, i) => {
              return (
                <Section
                  key={`faq-${faq.id}`}
                  sectionId={`faq-${toSlug(faq.question ?? 'Untitled')}`}
                  title={faq.question ?? 'Untitled'}
                  isChild={true}
                  icon={<Icon name="circle-question" />}
                  collapsed={i > 0}
                  renderCollapsedContent={true}
                  copy={false}
                  join={true}
                  offset={80}
                >
                  {faq.answer?.root?.children ? (
                    <RichText
                      content={faq.answer.root.children as Block[]}
                      readMore={extractBlogSlugUrlFromFaq(faq)}
                    />
                  ) : (
                    <p>No answer available</p>
                  )}
                </Section>
              )
            }),
          },
        ]
      : []

  // Final Verdict
  const finalVerdictSection: SectionItem[] = [
    {
      title: `Final Verdict`,
      content: (
        <div className={DEMOTED_HEADING_STYLES}>
          <RichText content={demoteHeadings((finalVerdict?.root?.children as Block[]) ?? [])} />
        </div>
      ),
    },
  ]

  // Structure
  const content: SectionItem[] = (() => {
    switch (pageTab) {
      case 'Alternatives':
        return [
          ...mainSummarySection,
          ...alternativesWithSummary,
          ...(finalVerdict && hasRichTextContent(finalVerdict.root?.children as Block[])
            ? finalVerdictSection
            : []),
          ...videoSection,
          ...faqSection,
        ]
      case 'Deals':
        return [...deals]
      case 'Comparison':
        return [...comparisonsSection]
      default:
        return []
    }
  })()

  return (
    <>
      <Meta
        modifiedTimestamp={modifiedTimestamp}
        publishedTimestamp={publishedTimestamp}
        ftc={true}
      />
      <AlternativeHero
        app={app}
        alternativeApps={orderedApps}
        timestamps={{ modified: modifiedTimestamp, published: publishedTimestamp }}
      />

      <article id="content" className="overlay">
        <Container
          sidebar={
            <>
              <Tree
                roots={content}
                header={
                  <AlternativeSidebar
                    key={app.id}
                    alternativeApps={orderedApps}
                    app={app}
                    category={categoryWithRecommendations}
                    menu={menu}
                  />
                }
              />
              <PreferredSource invert={true} />
              <div className="hidden flex-wrap items-center gap-y-2.5 md:flex">
                {(modifiedTimestamp || publishedTimestamp) && (
                  <Timestamp
                    modified={modifiedTimestamp}
                    published={publishedTimestamp}
                    lead="Last updated on"
                    trail="by"
                    className="pr-1"
                  />
                )}
                <Authors authors={authors} condensed={true} eagerImage={true} />
              </div>
              {/* Hide the generic deals cross-sell when this app is itself a
                  Gold/Platinum partner — its own deal/CTA already leads. */}
              {!isGoldOrPlatinumPartner(app.partnershipTier) && <DealsTrending />}
            </>
          }
          mobilePadding={false}
        >
          <TabToggle
            defaultValue="Alternatives"
            defaultIcon={<Icon name="alternative" />}
            deals={totalDeals}
            comparisonCount={totalComparisons}
          />

          {content.map((section, index) => (
            <div
              key={`${section.title}-${index}`}
              data-section-app={true}
              data-section-id={section.app ? section.app.slug : toSlug(section.title || '')}
              data-name={section.app ? section.app.name : section.title}
            >
              <Section
                title={section.title || ''}
                sectionId={section.app ? section.app.slug : section.slug ?? undefined}
                isParent={
                  section.title?.toLowerCase().includes('faq') ||
                  (section.children?.length ?? 0) > 0
                }
                icon={section.icon}
              >
                {section.content as ReactNode}
              </Section>
            </div>
          ))}
        </Container>

        <Container reduced={true}>
          <RelatedComparisons comparisons={serverComparisons} categoryId={primaryCategory.id} />
          <ExploreButton label="Explore all alternatives" link="/alternatives" />
        </Container>
      </article>
    </>
  )
}
